import { QueryParams } from 'common/urls/QueryParams';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useZipCode } from 'src/shared/hooks/useZipCode';
import { MIN_PARTIAL_SEARCH_LENGTH, SEARCH_DEBOUNCE_TIME, SEARCH_PARTIAL_DEBOUNCE_TIME } from 'src/store/search';
import { useCombinedSearchResults } from './useCombinedSearch';
import { useGetSearchResults } from './useGetSearch';
import { useSearchQueryReducer } from './useSearchQuery/useSearchQueryReducer';

export const useSearchDataProviderProps = () => {
  const updateSearchRef = useRef(0);

  const { changeSearchQuery, searchQuery } = useSearchQueryReducer();

  const {
    data,
    error,
    handleSearch,
    handleShowMoreShefs,
    handleShowMoreDishes,
    hasMoreResults,
    isMoreResultsLoading,
    loading,
    updateQueryParams,
    zipCode: activeZipCode,
  } = useGetSearchResults({ query: searchQuery });
  const combineSearchResults = useCombinedSearchResults({ query: searchQuery });
  const { availableToCustomers, loading: zipCodeDataLoading } = useZipCode(activeZipCode);

  const debouncedPartialSearchRef = useRef(debounce(handleSearch, SEARCH_PARTIAL_DEBOUNCE_TIME));
  const debouncedFullSearchRef = useRef(debounce(handleSearch, SEARCH_DEBOUNCE_TIME));

  const cancelDebouncedSearches = useCallback(() => {
    debouncedPartialSearchRef.current.cancel?.();
    debouncedFullSearchRef.current.cancel?.();
  }, []);

  const handleClearSearch = useCallback(() => {
    changeSearchQuery('');
    updateQueryParams({ [QueryParams.CONSUMER_SEARCH]: '' }, 'replaceIn');
  }, [updateQueryParams, handleSearch]);

  const handleQueryChange = useCallback(
    (newQuery: string, isPartialQuery: boolean) => {
      changeSearchQuery(newQuery);
      updateQueryParams({ [QueryParams.CONSUMER_SEARCH]: newQuery }, 'replaceIn');

      const trimmedQuery = newQuery.trim();
      if (isPartialQuery && trimmedQuery.length < MIN_PARTIAL_SEARCH_LENGTH) {
        debouncedPartialSearchRef.current.cancel?.();
        return Promise.resolve();
      }

      return (isPartialQuery ? debouncedPartialSearchRef.current : debouncedFullSearchRef.current)(
        newQuery,
        isPartialQuery
      );
    },
    [changeSearchQuery, updateQueryParams]
  );

  const refreshSearch = useCallback(() => {
    cancelDebouncedSearches();
    updateSearchRef.current = window.setTimeout(() => {
      debouncedPartialSearchRef.current = debounce(handleSearch, SEARCH_PARTIAL_DEBOUNCE_TIME);
      debouncedFullSearchRef.current = debounce(handleSearch, SEARCH_DEBOUNCE_TIME);
      handleSearch(searchQuery, false);
    }, 100);
  }, [cancelDebouncedSearches, handleSearch, searchQuery]);

  const handleDebouncedSearchCombined = useMemo(
    () => debounce(combineSearchResults.handleSearch, SEARCH_DEBOUNCE_TIME),
    []
  );
  const dataCombinedSearch = {
    ...combineSearchResults,
    activeZipCode,
    handleClearSearch: useCallback(() => {
      changeSearchQuery('');
      updateQueryParams({ [QueryParams.CONSUMER_SEARCH]: '' }, 'replaceIn');
      return combineSearchResults.handleSearch('');
    }, [updateQueryParams, combineSearchResults]),
    handleQueryChange: useCallback(
      (newQuery: string) => {
        changeSearchQuery(newQuery);
        updateQueryParams({ [QueryParams.CONSUMER_SEARCH]: newQuery }, 'replaceIn');
        return handleDebouncedSearchCombined(newQuery);
      },
      [updateQueryParams, handleDebouncedSearchCombined]
    ),
    searchQuery,
  };

  useEffect(() => {
    return () => {
      clearTimeout(updateSearchRef.current);
      cancelDebouncedSearches();
    };
  }, [cancelDebouncedSearches]);

  return {
    activeZipCode,
    data,
    dataCombinedSearch,
    error,
    loading,
    isMoreResultsLoading,
    hasMoreResults,
    handleQueryChange,
    handleShowMoreShefs,
    handleShowMoreDishes,
    handleClearSearch,
    isSearchHidden: !activeZipCode || (zipCodeDataLoading ? false : !availableToCustomers),
    refreshSearch,
  };
};
